/**
 * Client for `POST /api/intents/checkout-rebook` (tv-2na.8.5, W1-D3): the
 * in-chair "lock next session" at checkout. ONE server transaction creates the
 * next session (confirmed + firm, same piece/customer/artist), rolls the deposit
 * onto it (the rolled deposit's booking_id = the new booking, ledger appended)
 * and links it to the piece — or none of it.
 *
 * Same contract as the wrappers in api/intents.ts: never throws, sends the
 * facilitator's sim clock as `nowIso`, and returns a discriminated result whose
 * failure `kind` (intentErrorKind) the caller branches on:
 *   'slot_taken' — a firm booking overlaps the slot: pick another time;
 *   'rejected'   — the server refused this payload (4xx): retrying won't help;
 *   'transient'  — network / 5xx: safe to retry with the SAME ids (idempotent).
 */
import { authHeaders, isSigningIn, whenSignedIn } from '@/api/persist';
import { intentErrorKind, type IntentFailure } from '@/api/intents';
import { useSimulationStore } from '@/state/simulationStore';
import type { Booking, Deposit } from '@/domain/types';

export interface CheckoutRebookPayload {
  /** The session being checked out (in the chair today). */
  sourceBookingId: string;
  /** Client-minted id of the next session — the idempotency key. */
  bookingId: string;
  booking: Booking;
  /** Present when the deposit rolls forward onto the next session. */
  rolledDepositId?: string;
  rolledDeposit?: Deposit;
  nowIso?: string;
}

/** `alreadyExists`: an idempotent replay — the first call already committed. */
export type CheckoutRebookResult = { ok: true; alreadyExists: boolean } | IntentFailure;

export async function checkoutRebookIntent(payload: CheckoutRebookPayload): Promise<CheckoutRebookResult> {
  if (isSigningIn()) await whenSignedIn(); // B3: never on a previous persona's token mid-switch
  let res: Response;
  try {
    res = await fetch('/api/intents/checkout-rebook', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ ...payload, nowIso: payload.nowIso ?? useSimulationStore.getState().clockIso }),
    });
  } catch {
    // Never reached the server.
    return { ok: false, kind: 'transient', status: 0, error: 'network' };
  }

  const body = (await res.json().catch(() => undefined)) as
    | { alreadyExists?: unknown; error?: string; code?: string; message?: string }
    | undefined;
  if (res.ok) return { ok: true, alreadyExists: body?.alreadyExists === true };
  return {
    ok: false,
    kind: intentErrorKind(res.status, body),
    status: res.status,
    error: body?.error ?? `http-${res.status}`,
    ...(typeof body?.code === 'string' ? { code: body.code } : {}),
    ...(body?.message ? { message: body.message } : {}),
  };
}
